import { useEffect, useRef, useState } from 'react';
import { Search, Check, X as XIcon } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { API } from '../../App';

const ORANGE = '#ee7203';
const INK = '#1d1d1b';

// The orange outline used on the results list.
const GLASS_BORDER = '1px solid rgba(238,114,3,0.30)';

const MAX_LISTED = 10;
const compact = (value) => String(value || '').toLowerCase().replace(/\s+/g, '');

// Status dot colours — same palette as the live tracking tabs and status borders on the board itself
// (TransportOrderCard): en route = blue, on site = yellow, back to base / loading = green.
const LIVE_STATUS_COLOR = {
  en_route: '#3b82f6',
  at_drop: '#eab308',
  to_base: '#16a34a',
  loading: '#16a34a',
};

/** Resolves a result row's status indicator: a live tracking dot takes priority (today only),
 * otherwise the entry's own status (complete/failed) as a tick/cross, otherwise "TBC". */
const resultStatus = (item) => {
  if (item.liveStatus && LIVE_STATUS_COLOR[item.liveStatus]) {
    return { kind: 'dot', color: LIVE_STATUS_COLOR[item.liveStatus] };
  }
  if (item.entryStatus === 'complete') return { kind: 'icon', color: '#16a34a', Icon: Check };
  if (item.entryStatus === 'failed') return { kind: 'icon', color: '#ef4444', Icon: XIcon };
  return { kind: 'tbc', color: '#9333ea' };
};

const ResultStatusBadge = ({ item }) => {
  const s = resultStatus(item);
  if (s.kind === 'dot') return <span className="inline-block w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />;
  if (s.kind === 'icon') return <s.Icon size={12} className="flex-shrink-0" style={{ color: s.color }} />;
  return <span className="text-[9px] font-extrabold flex-shrink-0" style={{ color: s.color }}>TBC</span>;
};

/**
 * Finds every entry on the day's board that matches `query` in its customer name, any of its order numbers, its
 * postcode (spaces ignored) or its delivery location. Pure function — returns
 * [{ key, vehicleId, entryId, customer, postcode, order, registration, runLabel, entryStatus, liveStatus }].
 * `getLiveStatus(vehicle, entry)` is optional — when given (today's board only) it resolves the entry's live
 * tracking state (en_route/at_drop/to_base/loading) so the result can show the same blue/yellow/green dot as the board.
 */
export const findOnBoard = (vehicles, query, getLiveStatus) => {
  const text = String(query || '').trim().toLowerCase();
  const squashed = compact(query);
  if (!text) return [];
  const matches = [];
  (vehicles || []).forEach((vehicle) => {
    const runs = [{ label: 'Run 1', entries: vehicle.entries || [] }, ...(vehicle.runs || []).map((r, i) => ({ label: `Run ${r.run_number || i + 2}`, entries: r.entries || [] }))];
    runs.forEach((run) => {
      run.entries.forEach((entry) => {
        const orderNumbers = [entry.order_number, ...(entry.order_numbers || [])].filter(Boolean).map(String);
        const hit =
          String(entry.customer_name || '').toLowerCase().includes(text) ||
          orderNumbers.some((n) => n.toLowerCase().includes(text)) ||
          (squashed && compact(entry.postcode).includes(squashed)) ||
          String(entry.location || '').toLowerCase().includes(text);
        if (hit) {
          matches.push({
            key: `${vehicle.id}-${entry.id}`,
            vehicleId: vehicle.id,
            entryId: entry.id,
            customer: entry.customer_name || 'Unnamed customer',
            postcode: entry.postcode || '',
            order: orderNumbers[0] || '',
            registration: vehicle.vehicle_registration || '',
            runLabel: run.label,
            entryStatus: entry.status || null,
            liveStatus: getLiveStatus ? getLiveStatus(vehicle, entry) : null,
          });
        }
      });
    });
  });
  return matches;
};

const formatDay = (iso) => {
  const d = new Date(`${String(iso).slice(0, 10)}T12:00:00`);
  return Number.isNaN(d.getTime()) ? String(iso || '') : d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
};

/**
 * New Transport UI — the search bar above the vehicle selector in Map View. Type anything (customer name, order number,
 * postcode) then press one of the two buttons:
 *   [Today]  looks only at the day on screen and takes you to that vehicle, showing the customer on its run;
 *   [All]    looks across every day, jumps to the day and shows the customer.
 * Pressing Enter does the same as the first button. When more than one entry matches, a short list is shown to pick from.
 * Showing the customer uses the same "show this entry" animation as the Global Header search > Show on Transport.
 */
const TransportSearchBar = ({ vehicles, dayLabel = 'Today', onPickToday, onPickAll, getLiveStatus }) => {
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const finish = (list, pick) => {
    if (list.length === 1) { setOpen(false); pick(list[0]); return; }
    setItems(list);
    setOpen(true);
  };

  const searchToday = () => {
    const q = query.trim();
    if (!q) return;
    const list = findOnBoard(vehicles, q, getLiveStatus);
    if (list.length === 0) { setOpen(false); toast.info(`Nothing on this day matches "${q}"`); return; }
    finish(list.map((m) => ({ ...m, mode: 'today' })), (m) => onPickToday?.(m));
  };

  const searchAll = async () => {
    const q = query.trim();
    if (!q) return;
    setBusy(true);
    try {
      const res = await api.get(`${API}/transport/search`, { params: { q, limit: 40 } });
      const rows = (res.data?.results || []).filter((r) => r.date || r.collection_date);
      const list = rows.slice(0, MAX_LISTED * 3).map((r, i) => ({
        key: `${r.id || r.order_number || i}-${r.date || r.collection_date}`,
        mode: 'all',
        date: String(r.date || r.collection_date).slice(0, 10),
        entryKey: r.order_number || r.id,
        customer: r.customer_name || 'Unnamed customer',
        postcode: r.postcode || '',
        order: r.order_number || '',
        entryStatus: r.status || null,
      }));
      if (list.length === 0) { setOpen(false); toast.info(`Nothing found for "${q}" on any day`); return; }
      finish(list, (m) => onPickAll?.(m));
    } catch {
      toast.error('Search failed — please try again');
    } finally {
      setBusy(false);
    }
  };

  const choose = (item) => {
    setOpen(false);
    if (item.mode === 'all') onPickAll?.(item); else onPickToday?.(item);
  };

  // Plain white buttons with orange writing (and an orange outline).
  const whiteButton = {
    background: '#ffffff', border: `1.5px solid ${ORANGE}`, boxShadow: '0 2px 6px rgba(29,29,27,0.15)', color: ORANGE,
  };

  return (
    <div ref={wrapRef} className="relative flex-shrink-0" data-testid="transport-search-bar">
      <div className="flex items-center gap-1.5">
        <input
          data-testid="transport-search-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') searchToday(); if (e.key === 'Escape') setOpen(false); }}
          placeholder="Customer, order no. or postcode"
          className="min-w-0 flex-1 h-8 rounded-full px-3 text-[12px] font-medium outline-none border-none placeholder:text-slate-500"
          style={{ background: '#ffffff', color: '#111111' }}
        />
        <button
          type="button"
          data-testid="transport-search-today"
          onClick={searchToday}
          title={`Search ${dayLabel === 'Today' ? 'today' : 'the day on screen'} only`}
          className="h-8 px-2.5 rounded-full text-[11px] font-bold flex items-center gap-1 flex-shrink-0 hover:brightness-95 transition"
          style={whiteButton}
        >
          <Search size={12} />{dayLabel}
        </button>
        <button
          type="button"
          data-testid="transport-search-all"
          onClick={searchAll}
          disabled={busy}
          title="Search every day"
          className="h-8 px-2.5 rounded-full text-[11px] font-bold flex items-center gap-1 flex-shrink-0 hover:brightness-95 transition disabled:opacity-60"
          style={whiteButton}
        >
          <Search size={12} />{busy ? '…' : 'All'}
        </button>
      </div>

      {open && items.length > 1 && (
        <div
          className="absolute left-0 right-0 top-[calc(100%+6px)] z-[60] max-h-[280px] overflow-y-auto rounded-xl p-1"
          style={{ background: 'rgba(255,255,255,0.98)', border: GLASS_BORDER, boxShadow: '0 12px 30px rgba(29,29,27,0.30)' }}
          data-testid="transport-search-results"
        >
          {items.slice(0, MAX_LISTED * 3).map((item) => (
            <button
              type="button"
              key={item.key}
              onClick={() => choose(item)}
              className="w-full text-left rounded-lg px-2.5 py-1.5 hover:bg-orange-50 transition-colors"
              data-testid={`transport-search-result-${item.key}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="flex items-center gap-1.5 min-w-0">
                  <ResultStatusBadge item={item} />
                  <span className="text-[12px] font-bold truncate" style={{ color: INK }}>{item.customer}</span>
                </span>
                {item.order && <span className="text-[10px] font-semibold text-slate-500 flex-shrink-0">#{item.order}</span>}
              </div>
              <div className="text-[10px] text-slate-500 truncate pl-[14px]">
                {item.mode === 'all' ? formatDay(item.date) : `${item.registration} · ${item.runLabel}`}{item.postcode ? ` · ${item.postcode}` : ''}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default TransportSearchBar;
